import type * as rtk from "@reduxjs/toolkit";
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { config } from "../config";

// Interfaces
export interface File {
  _id: { $oid: string };          // Object ID in MongoDB format
  file_name: string;              // Name of the file
  project_id: { $oid: string };   // Project ID in MongoDB format
  topic_id: { $oid: string };     // Topic ID in MongoDB format
  created_at: string;             // ISO string for creation timestamp
  last_updated: string;           // ISO string for last update timestamp
}

export interface FilesState {
  files: File[];
  loading: boolean;
  error: string | null;
}

export interface ServerResponse {
  status: number;
  short_message: string;
  long_message: string;
  data: any;
}

interface FetchRequestParams {
  token: string;
  topicId: string;
  projectId: string;
  fileId?: string;
  fileName?: string;
  content?: string;
  [key: string]: any;
}

interface CreateFileParams {
  token: string;
  topicId: string;
  projectId: string;
  fileName: string;
}

// Initial State
const initialState: FilesState = {
  files: [],
  loading: false,
  error: null,
};

// Base URL function
export const getBaseUrl = () => {
  if (process.env.NODE_ENV === 'production') {
    return 'https://api.docloo.co.uk';
  } else {
    return 'http://192.168.53:3011';
  }
};
const backendUrl = getBaseUrl();

// Utility: Handle fetch errors
const handleFetchError = (error: unknown): ServerResponse => {
  if (error instanceof TypeError) {
    return {
      status: 0,
      short_message: 'Network error: Please check your connection.',
      long_message: 'Network error: Unable to fetch data due to connectivity issues.',
      data: null,
    };
  }
  const message = (error as Error).message || 'Unknown error occurred.';
  return { status: 0, short_message: message, long_message: message, data: null };
};

// Thunks
// Fetch files list
export const fetchFiles = createAsyncThunk(
  'files/fetchFiles',
  async ({ token, topicId, projectId }: FetchRequestParams, { rejectWithValue }) => {
    try {
      const response = await fetch(`${backendUrl}/files/list`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ topic_id: topicId, project_id: projectId }),
      });

      if (!response.ok) {
        const errorData: ServerResponse = await response.json();
        return rejectWithValue(errorData.short_message || 'Failed to fetch files.');
      }

      const data = await response.json();
      return data.data as File[];
    } catch (error) {
      return rejectWithValue(handleFetchError(error).short_message);
    }
  }
);

// Retrieve a specific file
export const retrieveFile = createAsyncThunk(
  'files/retrieveFile',
  async ({ token, topicId, projectId, fileId }: FetchRequestParams, { rejectWithValue }) => {
    try {
      const response = await fetch(`${backendUrl}/files/retrieve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ topic_id: topicId, project_id: projectId, file_id: fileId }),
      });

      if (!response.ok) {
        const errorData: ServerResponse = await response.json();
        return rejectWithValue(errorData.short_message || 'Failed to retrieve file.');
      }

      const data = await response.json();
      return data.data as File;
    } catch (error) {
      return rejectWithValue(handleFetchError(error).short_message);
    }
  }
);

// Create a new file
export const createFile = createAsyncThunk(
  'files/createFile',
  async ({ token, topicId, projectId, fileName }: CreateFileParams, { rejectWithValue }) => {
    try {
      const response = await fetch(`${backendUrl}/files/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ file_name: fileName+".mdx"
          , topic_id: topicId, project_id: projectId }),
      });

      if (!response.ok) {
        const errorData: ServerResponse = await response.json();
        return rejectWithValue(errorData.short_message || 'Failed to create file.');
      }

      const data = await response.json();
      return data.data as File;
    } catch (error) {
      return rejectWithValue(handleFetchError(error).short_message);
    }
  }
);

// Delete a file
export const deleteFile = createAsyncThunk(
  'files/deleteFile',
  async ({ token, topicId, projectId, fileId }: FetchRequestParams, { rejectWithValue }) => {
    try {
      const response = await fetch(`${backendUrl}/files/delete`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ topic_id: topicId, project_id: projectId, file_id: fileId }),
      });

      if (!response.ok) {
        const errorData: ServerResponse = await response.json();
        return rejectWithValue(errorData.short_message || 'Failed to delete file.');
      }

      return { topicId, projectId, fileId };
    } catch (error) {
      return rejectWithValue(handleFetchError(error).short_message);
    }
  }
);

// Update file content
export const updateFileContent = createAsyncThunk(
  'files/updateFileContent',
  async ({ token, topicId, projectId, fileId, content }: FetchRequestParams, { rejectWithValue }) => {

    const formData = new FormData();
      formData.append('content', content || '');

    try {
      const response = await fetch(
        `${backendUrl}/files/update?project_id=${projectId}&topic_id=${topicId}&file_id=${fileId}`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        }
      );

      if (!response.ok) {
        const errorData: ServerResponse = await response.json();
        return rejectWithValue(errorData.short_message || 'Failed to update file content.');
      }

      const data = await response.json();
      return data.data as File;
    } catch (error) {
      return rejectWithValue(handleFetchError(error).short_message);
    }
  }
);

// Redux Slice
const filesSlice = createSlice({
  name: 'files',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchFiles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFiles.fulfilled, (state, action: PayloadAction<File[]>) => {
        state.loading = false;
        state.files = action.payload;
      })
      .addCase(fetchFiles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(retrieveFile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(retrieveFile.fulfilled, (state, action: PayloadAction<File>) => {


        console.log("retrieved file", action.payload);
        state.loading = false;
        const retrievedFile = action.payload;
       
      })
      .addCase(retrieveFile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createFile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createFile.fulfilled, (state, action: PayloadAction<File>) => {
        state.loading = false;
        state.files.push(action.payload);
      })
      .addCase(createFile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(deleteFile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteFile.fulfilled, (state, action: PayloadAction<{ topicId: string; projectId: string; fileId: string }>) => {
        state.loading = false;
        state.files = state.files.filter(file => file._id.$oid !== action.payload.fileId);
      })
      .addCase(deleteFile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateFileContent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateFileContent.fulfilled, (state, action: PayloadAction<File>) => {
        state.loading = false;
        const updatedFile = action.payload;
       
      })
      .addCase(updateFileContent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export default filesSlice.reducer;
